import { useEffect, useMemo, useState } from 'react';

import { company } from '~/company/Company';
import CityDrinksStepData from '~/company/configs/citydrinks/onboarding';
import DefaultStepData from '~/company/configs/default/onboarding';
import { userStore } from '~/stores';

let timeout: ReturnType<typeof setTimeout>;

export const useStepData = () => {
  const [selectedScreenIndex, setSelectedScreenIndex] = useState(0);
  const [isTimerAlive, setIsTimerAlive] = useState(
    company.hasOnboardingAutoplay,
  );

  // Memoised because both branches are factories: without this `STEPS_DATA` is
  // a new object on every render, so an effect that honestly declares it as a
  // dependency would re-run on every render.
  const STEPS_DATA = useMemo(
    () =>
      company.variant({
        '1': CityDrinksStepData(),
        '2': DefaultStepData(),
      }),
    [],
  );

  /**
   * Warms the next frame while the current one is on screen.
   *
   * Steps are painted as CSS backgrounds, so the browser does not request a
   * frame until its step is rendered — without this the transition shows an
   * empty panel on a slow connection. One frame ahead is enough: autoplay
   * leaves five seconds per step.
   */
  useEffect(() => {
    const nextImage = STEPS_DATA.stepsData[selectedScreenIndex + 1]?.img;
    if (!nextImage) {
      return;
    }
    const image = new Image();
    image.src = nextImage;
  }, [selectedScreenIndex, STEPS_DATA]);

  useEffect(() => {
    if (
      selectedScreenIndex < STEPS_DATA.stepsData.length - 1 &&
      isTimerAlive &&
      userStore.personalData.isAdult
    ) {
      timeout = setTimeout(() => {
        setSelectedScreenIndex((current) => current + 1);
      }, 5000);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [selectedScreenIndex, userStore.personalData.isAdult]);

  return {
    handleChangeScreenIndex: setSelectedScreenIndex,
    handleTimerAlive: setIsTimerAlive,
    currentStep: selectedScreenIndex,
    stepsNumber: STEPS_DATA.stepsData.length,
    currentStepData: STEPS_DATA.stepsData[selectedScreenIndex],
    additionalData: STEPS_DATA.additionalData,
  };
};
